import { Component, type ReactNode } from 'react';
import { Button, Result } from 'antd';

type Props = {
  children: ReactNode;
  resetKey: string;
  /** What still works while the page is down, said under the title. */
  subTitle?: string;
};

/**
 * Keeps the app on screen when a page fails (most often a page file that did not load on a weak
 * connection: "Failed to fetch dynamically imported module"). Without it React removes the whole
 * app and leaves a blank white page. Moving to another page (`resetKey`, the path) tries again;
 * pages are never remounted otherwise.
 */
export class PageErrorBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  // A page whose file did not load opens by itself once the signal is back (round 20, Deepa).
  private onOnline = () => {
    if (this.state.failed) window.location.reload();
  };

  override componentDidMount() {
    window.addEventListener('online', this.onOnline);
  }

  override componentWillUnmount() {
    window.removeEventListener('online', this.onOnline);
  }

  override componentDidUpdate(previous: Props) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Result
        status="warning"
        title="This page didn't load"
        subTitle={this.props.subTitle ?? 'Check the internet connection, then reload.'}
        extra={
          <Button
            type="primary"
            size="large"
            style={{ minHeight: 48 }}
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        }
      />
    );
  }
}
